import React, { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getCurrentUser, getIdTokenClaims } from "../../common/auth";
import { getLandingUrl } from "../../common/landingLead";
import {
  hasCompletedOnboarding,
  hasSubmittedForApproval,
  userKeyFromClaims,
} from "../../common/onboarding";
import { fetchTenantAccessState, syncOnboardingFlagsFromTenant } from "../../common/tenantAccess";
import "./Onboarding.css";

const STEPS = [
  { path: "cuenta", label: "Cuenta" },
  { path: "workspace", label: "Workspace" },
  { path: "contacto", label: "Contacto" },
  { path: "equipo", label: "Equipo" },
];

export default function OnboardingShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentStep = STEPS.findIndex((s) => location.pathname.includes(s.path));

  useEffect(() => {
    let cancelled = false;
    const user = getCurrentUser();
    if (!user) return undefined;

    user.getSession(async (err, session) => {
      if (cancelled || err || !session?.isValid()) return;
      try {
        const claims = await getIdTokenClaims();
        if (cancelled) return;
        const userKey = userKeyFromClaims(claims);
        const state = await fetchTenantAccessState();
        if (state.approved) {
          syncOnboardingFlagsFromTenant(state.tenant, userKey);
          navigate("/Home", { replace: true });
          return;
        }
        if (
          state.pending ||
          (hasSubmittedForApproval(userKey) && !hasCompletedOnboarding(userKey))
        ) {
          navigate("/solicitud-pendiente", { replace: true });
        }
      } catch {
        /* ignore */
      }
    });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="ob-shell">
      <header className="ob-header">
        <a href={getLandingUrl()} className="ob-logo" aria-label="Tagnize">
          <img
            src={`${process.env.PUBLIC_URL}/logo_header.png`}
            alt="TagNize"
            className="ob-logo-img"
          />
        </a>

        <nav className="ob-stepper" aria-label="Progreso del onboarding">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.path}>
              <div
                className={`ob-step ${
                  idx < currentStep ? "done" : idx === currentStep ? "active" : ""
                }`}
              >
                <div className="ob-step-circle">
                  {idx < currentStep ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span className="ob-step-label">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`ob-step-line ${idx < currentStep ? "done" : ""}`} />
              )}
            </React.Fragment>
          ))}
        </nav>

        <Link to="/login" className="ob-header-login">
          Iniciar sesión
        </Link>
      </header>

      <main className="ob-main">
        <div className="ob-card">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
